import React from 'react';
import { X, Type, ListChecks, Star, Mail, Hash, Image, Calendar, Phone, CheckSquare, AlignLeft, Users, ToggleLeft, ChevronDown, Sliders, Clock, Link2, ArrowUpDown } from 'lucide-react';

const QuestionTypeSelector = ({ isOpen, onClose, onSelectType }) => {
  const questionTypes = [
    {
      type: 'short-text',
      icon: Type,
      label: 'Short Text',
      description: 'Single line text (max 100 chars)',
      color: 'blue'
    },
    {
      type: 'long-text',
      icon: AlignLeft,
      label: 'Long Text',
      description: 'Multi-line text answer',
      color: 'blue'
    },
    {
      type: 'multiple-choice',
      icon: ListChecks,
      label: 'Multiple Choice',
      description: 'Select one from options',
      color: 'purple'
    },
    {
      type: 'checkbox',
      icon: CheckSquare,
      label: 'Checkboxes',
      description: 'Select multiple options',
      color: 'green'
    },
    {
      type: 'dropdown',
      icon: ChevronDown,
      label: 'Dropdown',
      description: 'Select from dropdown list',
      color: 'violet'
    },
    {
      type: 'yes-no',
      icon: ToggleLeft,
      label: 'Yes/No',
      description: 'Simple binary choice',
      color: 'emerald'
    },
    {
      type: 'rating',
      icon: Star,
      label: 'Rating',
      description: 'Star or numeric rating',
      color: 'yellow'
    },
    {
      type: 'scale',
      icon: Sliders,
      label: 'Linear Scale',
      description: '1-10 scale slider',
      color: 'cyan'
    },
    {
      type: 'ranking',
      icon: ArrowUpDown,
      label: 'Ranking',
      description: 'Rank options by preference',
      color: 'fuchsia'
    },
    {
      type: 'email',
      icon: Mail,
      label: 'Email',
      description: 'Email address input',
      color: 'red'
    },
    {
      type: 'number',
      icon: Hash,
      label: 'Number',
      description: 'Numeric input',
      color: 'indigo'
    },
    {
      type: 'phone',
      icon: Phone,
      label: 'Phone Number',
      description: 'Phone number input',
      color: 'teal'
    },
    {
      type: 'url',
      icon: Link2,
      label: 'Website URL',
      description: 'Website address input',
      color: 'sky'
    },
    {
      type: 'date',
      icon: Calendar,
      label: 'Date',
      description: 'Date picker',
      color: 'pink'
    },
    {
      type: 'time',
      icon: Clock,
      label: 'Time',
      description: 'Time picker',
      color: 'amber'
    },
    {
      type: 'gender',
      icon: Users,
      label: 'Gender',
      description: 'Gender selection',
      color: 'rose'
    },
    {
      type: 'image',
      icon: Image,
      label: 'Image Choice',
      description: 'Choose from images',
      color: 'orange'
    }
  ];

  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    red: 'bg-red-50 text-red-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    teal: 'bg-teal-50 text-teal-600',
    pink: 'bg-pink-50 text-pink-600',
    orange: 'bg-orange-50 text-orange-600',
    violet: 'bg-violet-50 text-violet-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    cyan: 'bg-cyan-50 text-cyan-600',
    fuchsia: 'bg-fuchsia-50 text-fuchsia-600',
    sky: 'bg-sky-50 text-sky-600',
    amber: 'bg-amber-50 text-amber-600',
    rose: 'bg-rose-50 text-rose-600'
  };

  const handleSelect = (type) => {
    onSelectType(type);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 md:p-6">
        <div className="bg-white rounded-xl sm:rounded-2xl border-2 border-gray-300 w-full max-w-sm sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-screen sm:max-h-[85vh] md:max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-5 md:p-6 border-b border-gray-200">
            <div>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">Add Question</h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">Choose a question type to get started</p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={24} className="text-gray-400" />
            </button>
          </div>

          {/* Question Types Grid */}
          <div className="p-4 sm:p-5 md:p-6 overflow-y-auto max-h-[calc(100vh-150px)] sm:max-h-[calc(85vh-120px)] md:max-h-[calc(90vh-120px)]">
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
              {questionTypes.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.type}
                    onClick={() => handleSelect(item.type)}
                    className="flex flex-col items-start p-3 sm:p-4 border-2 border-gray-200 rounded-lg sm:rounded-xl hover:border-blue-600  transition-all group text-left"
                  >
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center mb-2 sm:mb-3 ${colorClasses[item.color]} group-hover:scale-110 transition-transform`}>
                      <Icon size={20} className="sm:w-6 sm:h-6" />
                    </div>
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1 group-hover:text-blue transition-colors">
                      {item.label}
                    </h3>
                    <p className="text-[10px] sm:text-xs text-gray-500 line-clamp-2">
                      {item.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuestionTypeSelector;